// Iter XX.133 — Recognition-intro copy, hoisted so both the
// ParentEntryGate and the eager-PCE fallback can reference the same
// canonical lines. Any change here changes both surfaces.
export const RECOGNITION_LINE_1 =
  "You wouldn't be here if cricket didn't matter to you.";
export const RECOGNITION_LINE_2 =
  "Let's make sure all that effort turns into improvement.";

// Iter XX.129 — Progressive Commitment Engine client helpers.
//
// Thin wrapper around /api/pce/*. Also handles the localStorage flag
// that guarantees the engine is a one-shot experience per browser
// (never replays for a returning visitor). Eligibility is otherwise
// driven by the server flag (`progressive_commitment_engine_enabled`)
// via useFlag.
import axios from "axios";
import { getBrowserId } from "./browserId";
import { API } from "./api";
import { webOnlyWithCredentials } from "./nativePlatform";

const PCE_DONE_KEY = "g_pce_v1_done";

// Iter XX.133 — Preload cache. Kicks off /api/pce/start in parallel
// with the ParentEntryGate rendering so that by the time
// QuestionEngineCard mounts, the first question is already in
// memory. Zero visible loading state.
const _preloadCache = new Map();

export function pcePreload(sessionId, entryPoint = "chat") {
  if (!sessionId) return null;
  const key = `pce_start:${sessionId}`;
  if (_preloadCache.has(key)) return _preloadCache.get(key);
  const promise = _post("/pce/start", {
    session_id: sessionId,
    browser_id: getBrowserId(),
    entry_point: entryPoint,
  }).catch((e) => {
    // Preload failures are silent — the retry path (pceStart called
    // from QuestionEngineCard on mount) will surface the same error
    // through the normal error state.
    _preloadCache.delete(key);
    throw e;
  });
  _preloadCache.set(key, promise);
  return promise;
}

export function shouldShowPce({ flagOn, hasFirstMessage, isReturning }) {
  if (!flagOn) return false;
  if (hasFirstMessage) return false;
  if (isReturning) return false;
  try {
    if (localStorage.getItem(PCE_DONE_KEY) === "1") return false;
  } catch { /* noop */ }
  return true;
}

export function markPceDone() {
  try { localStorage.setItem(PCE_DONE_KEY, "1"); } catch { /* noop */ }
}

export function resetPce() {
  try { localStorage.removeItem(PCE_DONE_KEY); } catch { /* noop */ }
}

async function _post(path, body) {
  const bid = getBrowserId();
  const res = await axios.post(`${API}${path}`, body, {
    headers: { "X-Browser-Id": bid || "" },
    withCredentials: webOnlyWithCredentials(),
    validateStatus: () => true,
  });
  if (res.status >= 400) {
    const detail = res.data?.detail || `pce_${path}_${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.data;
}

export function pceStart(sessionId, entryPoint = "chat") {
  // Iter XX.133 — Serve from preload cache if available (usually is:
  // the gate fires pcePreload as soon as the user picks an audience).
  const key = `pce_start:${sessionId}`;
  const cached = _preloadCache.get(key);
  if (cached) {
    _preloadCache.delete(key);
    return cached;
  }
  return _post("/pce/start", {
    session_id: sessionId,
    browser_id: getBrowserId(),
    entry_point: entryPoint,
  });
}

export function pceAnswer(sessionId, questionId, answerId, latencyMs) {
  return _post("/pce/answer", {
    session_id: sessionId,
    question_id: questionId,
    answer_id: answerId,
    latency_ms: latencyMs,
  });
}

export function pceSkip(sessionId, reason = "user_skip") {
  return _post("/pce/skip", { session_id: sessionId, reason });
}
